// PreventiApp · Sprint 5 · Métricas mínimas de uso del piloto
//
// Reglas estrictas de privacidad:
// - NUNCA se registran: nombres, correos, IDs de estudiantes, relatos,
//   contenido de casos, audios, imágenes, filtros con valor de texto libre.
// - SOLO se registran acciones funcionales con datos categóricos anonimizados:
//   nombre del evento, nombre del filtro (no su valor libre), pestaña, tiempos.
// - Identificador de sesión: uuid random guardado en sessionStorage (no persistente).
// - Rol y colegio_id se envían **hasheados** en cliente para agrupar sin exponer identidades.
//
// Transporte: navigator.sendBeacon (best-effort, no bloquea UI). Fallback a fetch keepalive.
// Endpoint backend: POST /api/metrics/track — recibe batch de eventos.
// Failsafe: si el backend no responde, se descartan (no se almacenan localmente para no crecer).
import React, { useEffect } from 'react';

const SESSION_KEY = 'preventiapp.metrics.sid';
const HASH_KEY = 'preventiapp.metrics.hash';

function ensureSid() {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = (crypto.randomUUID && crypto.randomUUID()) || Math.random().toString(36).slice(2);
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

async function shortHash(input) {
  // Hash irreversible (SHA-256 → primeros 8 hex chars). Suficiente para agrupar
  // por rol/colegio sin poder revertir a un id concreto.
  if (!input) return null;
  const data = new TextEncoder().encode(String(input));
  const buf = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf).slice(0, 4))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function post(events) {
  if (!events?.length) return;
  const body = JSON.stringify({ events });
  const url = (process.env.REACT_APP_BACKEND_URL || '') + '/api/metrics/track';
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
  } catch {
    // fallthrough
  }
  fetch(url, {
    method: 'POST',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body,
  }).catch(() => {});
}

const queue = [];
let flushTimer = null;

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    const batch = queue.splice(0, queue.length);
    flushTimer = null;
    post(batch);
  }, 1500);
}

/**
 * Registra un evento de uso mínimo, anonimizado.
 *
 * @param {string} event - nombre categórico (ej. 'accion_hoy_view', 'tab_change', 'filter_use')
 * @param {object} attrs - solo atributos categóricos (ej. { tab: 'prioridades' }). NO PII.
 */
export async function track(event, attrs = {}) {
  try {
    const sid = ensureSid();
    let roleHash = sessionStorage.getItem(HASH_KEY + '.rol');
    let colegioHash = sessionStorage.getItem(HASH_KEY + '.col');
    // El hash lo escribe `setActor()` al login; si no está, lo dejamos null.
    queue.push({
      event,
      attrs: sanitizeAttrs(attrs),
      sid,
      role_hash: roleHash || null,
      colegio_hash: colegioHash || null,
      ts: Date.now(),
    });
    scheduleFlush();
  } catch {
    // Nunca romper la UI por métricas.
  }
}

/**
 * Guarda hashes de rol y colegio para adjuntar en cada evento. Se llama tras el login.
 */
export async function setActor({ rol, colegio_id }) {
  try {
    const rh = await shortHash(rol);
    const ch = await shortHash(colegio_id);
    if (rh) sessionStorage.setItem(HASH_KEY + '.rol', rh);
    if (ch) sessionStorage.setItem(HASH_KEY + '.col', ch);
  } catch {
    // ignore
  }
}

export function clearActor() {
  sessionStorage.removeItem(HASH_KEY + '.rol');
  sessionStorage.removeItem(HASH_KEY + '.col');
  sessionStorage.removeItem(SESSION_KEY);
}

/**
 * Descarta cualquier valor que no sea una primitiva simple y limita el largo.
 * No se admiten objetos anidados ni arrays largos: la política es "atributos
 * categóricos cortos y ya conocidos".
 */
function sanitizeAttrs(attrs) {
  const clean = {};
  for (const k of Object.keys(attrs || {})) {
    const v = attrs[k];
    if (v === null || v === undefined) continue;
    if (typeof v === 'string') {
      // Cortar a 32 chars y solo dejar valores tipo slug (evita relatos libres).
      const slug = v.slice(0, 32);
      if (/^[a-z0-9_.:-]{0,32}$/i.test(slug)) clean[k] = slug;
    } else if (typeof v === 'number' && Number.isFinite(v)) {
      clean[k] = v;
    } else if (typeof v === 'boolean') {
      clean[k] = v;
    }
  }
  return clean;
}

/** Hook para reportar el tiempo de carga inicial de una vista */
export function useTrackLoad(event, loading, extraAttrs = {}) {
  useEffect(() => {
    if (loading) return;
    const t = Math.round(performance.now());
    track(event, { load_ms: t, ...extraAttrs });
  }, [loading]);
}
